// API + polling logic for the "Check My Client Version" feature.
//
// Flow: POST /client_version/check to mint a key and get the tuxclient:/// URI,
// hand that URI to the local Parallels client (window.location.href), then
// short-poll /client_version/result until the client calls back or we time out.

import { Settings } from "../../settings";

// Status states surfaced to the UI via the onStatusChange callback.
export const VersionCheckState = Object.freeze({
    Starting: "starting",
    Polling: "polling",
    Received: "received",
    // The client answered, but not with a version we can compare (it needs a
    // major, a minor and a build). Distinct from NotDetected, where no client
    // answered at all.
    VersionUnknown: "version_unknown",
    NotDetected: "not_detected",
    Error: "error",
});

// Parallels "os" codes (per the Parallels URL-scheme docs) -> display labels.
const OS_LABELS = Object.freeze({
    0: "Windows",
    1: "Linux",
    2: "macOS",
    4: "Android",
    5: "iOS",
});

export function osLabel(osCode) {
    return OS_LABELS[osCode];
}

function getRoutePath(route) {
    return `${process.env.PUBLIC_URL}/${route}`;
}

// When the login session has expired, the API returns 401 with a Location header
// pointing at the landing page. Send the user there to re-authenticate instead of
// surfacing a generic error. Returns true when a redirect was initiated.
function redirectToLoginIfUnauthorized(response) {
    if (response.status !== 401) {
        return false;
    }
    window.location.href = response.headers.get("Location") ?? `${process.env.PUBLIC_URL}/`;
    return true;
}

export async function startVersionCheck() {
    const response = await fetch(getRoutePath("client_version/check"), {
        method: "POST",
        credentials: "same-origin",
    });
    if (redirectToLoginIfUnauthorized(response)) {
        return null; // session expired -> navigating to login
    }
    if (!response.ok) {
        throw new Error(`client_version/check failed: ${response.status}`);
    }
    return response.json(); // { key, uri }
}

export async function fetchVersionResult(key) {
    const url = getRoutePath("client_version/result") + `?key=${encodeURIComponent(key)}`;
    const response = await fetch(url, { credentials: "same-origin" });
    if (redirectToLoginIfUnauthorized(response)) {
        return null; // session expired -> navigating to login
    }
    if (!response.ok) {
        throw new Error(`client_version/result failed: ${response.status}`);
    }
    return response.json();
}

function pollForResult(key, onStatusChange) {
    return new Promise((resolve) => {
        const finish = () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            resolve();
        };

        const intervalId = setInterval(async () => {
            let result;
            try {
                result = await fetchVersionResult(key);
            } catch {
                // Transient error - keep polling until the timeout fires.
                return;
            }

            if (result === null) {
                // Session expired -> redirecting to login; stop polling.
                finish();
                return;
            }

            if (result.status === VersionCheckState.Received) {
                finish();
                onStatusChange({
                    state: VersionCheckState.Received,
                    version: result.version,
                    outdated: result.outdated,
                    latestVersion: result.latest_version,
                    minVersion: result.min_version,
                    osLabel: osLabel(result.os),
                });
            } else if (result.status === VersionCheckState.VersionUnknown) {
                // Client is installed, but its version is not comparable.
                finish();
                onStatusChange({
                    state: VersionCheckState.VersionUnknown,
                    osLabel: osLabel(result.os),
                });
            } else if (result.status === "not_found") {
                // Key expired (TTL) before any callback -> client not detected.
                // Terminal, so a shorter TTL than the poll timeout still resolves fast.
                finish();
                onStatusChange({ state: VersionCheckState.NotDetected });
            }
        }, Settings.clientVersionCheck.interval);

        const timeoutId = setTimeout(() => {
            finish();
            onStatusChange({ state: VersionCheckState.NotDetected });
        }, Settings.clientVersionCheck.timeout);
    });
}

// Entry point called by the UI. onStatusChange receives objects of the shape
// { state, version?, outdated?, latestVersion?, minVersion?, osLabel? } as the
// check progresses.
export async function checkClientVersion(onStatusChange) {
    onStatusChange({ state: VersionCheckState.Starting });

    let key;
    let uri;
    try {
        const started = await startVersionCheck();
        if (started === null) {
            // Session expired -> redirecting to login; leave the UI as-is.
            return;
        }
        ({ key, uri } = started);
    } catch {
        onStatusChange({ state: VersionCheckState.Error });
        return;
    }

    onStatusChange({ state: VersionCheckState.Polling });

    // Hand off to the local Parallels client (triggers the browser open prompt).
    window.location.href = uri;

    await pollForResult(key, onStatusChange);
}
